<template>
  <div
    aria-label="Retour définitif"
    role="main"
  >
    <!-- Confirmation Modal -->
    <b-modal
      ref="confirmationModal"
      title="Confirmation du retour définitif"
      size="lg"
      no-close-on-backdrop
      no-close-on-esc
      @ok="confirmAndSubmit"
    >
      <template #modal-header="{ close }">
        <div class="w-100 d-flex justify-content-between align-items-center">
          <h4 class="modal-title mb-0 font-weight-bolder">
            <feather-icon
              icon="CheckCircleIcon"
              class="mr-2 text-success"
            />
            Confirmation du retour définitif
          </h4>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="close()"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </template>

      <div class="confirmation-summary p-1">
        <h5 class="mb-3 text-primary border-bottom pb-1">
          Type de retour: {{ getTargetStatusLabel() }}
        </h5>

        <b-row>
          <b-col
            cols="12"
            md="6"
          >
            <b-card
              variant="light"
              class="mb-3 border-left-danger border-left-3 shadow-none bg-light"
            >
              <h6 class="text-danger mb-2 font-weight-bold uppercase">
                <feather-icon
                  icon="SlashIcon"
                  class="mr-1"
                />
                Retour définitif
              </h6>
              <div class="d-flex justify-content-between align-items-baseline">
                <span class="text-muted">Nombre de colis:</span>
                <span class="h4 mb-0 font-weight-bolder text-danger">{{ definitiveReturnOrders.length }}</span>
              </div>
            </b-card>
          </b-col>

          <b-col
            cols="12"
            md="6"
          >
            <b-card
              variant="light"
              class="mb-3 border-left-warning border-left-3 shadow-none bg-light"
            >
              <h6 class="text-warning mb-2 font-weight-bold uppercase">
                <feather-icon
                  icon="RefreshCwIcon"
                  class="mr-1"
                />
                Relance
              </h6>
              <div class="d-flex justify-content-between align-items-baseline">
                <span class="text-muted">Nombre de colis:</span>
                <span class="h4 mb-0 font-weight-bolder text-warning">{{ relanceOrders.length }}</span>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <b-alert
          show
          variant="info"
          class="mt-1"
        >
          <div class="alert-body d-flex align-items-center">
            <feather-icon
              icon="InfoIcon"
              class="mr-2"
              size="20"
            />
            <span>Êtes-vous sûr de vouloir valider ces retours? Les statuts des colis seront mis à jour immédiatement.</span>
          </div>
        </b-alert>
      </div>

      <template #modal-footer="{ ok, cancel }">
        <b-button
          variant="outline-secondary"
          @click="cancel()"
        >
          <feather-icon
            icon="XIcon"
            class="mr-1"
          />
          Annuler
        </b-button>
        <b-button
          variant="success"
          :disabled="isLoading"
          class="px-3"
          @click="ok()"
        >
          <b-spinner
            v-if="isLoading"
            small
            class="mr-1"
          />
          <feather-icon
            v-else
            icon="CheckIcon"
            class="mr-1"
          />
          {{ isLoading ? 'Traitement...' : 'Confirmer la validation' }}
        </b-button>
      </template>
    </b-modal>

    <!-- Top Controls Card -->
    <b-card
      class="mb-2 shadow-sm"
      body-class="p-2"
    >
      <!-- Repository Selection (Admin/Superadmin only) -->
      <b-row v-if="['admin', 'superadmin', 'customer_support'].includes(user.role)">
        <b-col cols="12">
          <b-form-group
            label="Dépôt"
            label-for="select-repository"
            class="mb-2"
          >
            <v-select
              v-model="repository"
              :clearable="false"
              placeholder="Sélectionner un dépôt"
              label="name"
              :options="repositories"
              input-id="select-repository"
              @input="loadOrders"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Scan Inputs - Two separate fields -->
      <!-- <b-form-group
        label="Référence de colis (Scannez ou saisissez la référence)"
        label-for="scan-input"
        class="mb-2"
      >
        <b-input-group class="input-group-merge mb-2">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="XCircleIcon"
              class="text-danger"
            />
          </b-input-group-prepend>
          <b-form-input
            id="input-definitive-ref"
            ref="definitiveInput"
            v-model="definitiveRef"
            placeholder="Scanner ou saisir la référence du colis pour retour définitif"
            :disabled="!repositoryId"
            @change="scanForDefinitive"
          />
        </b-input-group>
        <b-input-group class="input-group-merge">
          <b-input-group-prepend is-text>
            <feather-icon
              icon="RefreshCwIcon"
              class="text-warning"
            />
          </b-input-group-prepend>
          <b-form-input
            id="input-relance-ref"
            ref="relanceInput"
            v-model="relanceRef"
            placeholder="Scanner ou saisir la référence du colis relance"
            :disabled="!repositoryId"
            @change="scanForRelance"
          />
        </b-input-group>
      </b-form-group> -->
    </b-card>

    <div v-if="repositoryId">
      <!-- Main Available Orders Table -->
      <b-card
        class="mb-2 border"
        body-class="p-0 overflow-auto"
      >
        <div class="table-header-title-simple">
          <h5 class="mb-0 d-flex align-items-center">
            <feather-icon
              icon="PackageIcon"
              size="18"
              class="mr-2"
            />
            Colis en anomalie de livraison :
            <span class="text-primary font-weight-bold ml-2">{{ availableOrders.length }}</span>
          </h5>
        </div>

        <vue-good-table
          :columns="mainColumns"
          :rows="availableOrders"
          :search-options="{
            enabled: false
          }"
          :pagination-options="{
            enabled: false
          }"
          style-class="vgt-table pro-table"
          :max-height="'580px'"
        >
          <!-- Column Filters -->
          <template
            slot="table-column"
            slot-scope="props"
          >
            <span v-if="props.column.field !== 'action'">
              {{ props.column.label }}
            </span>
            <span v-else>
              {{ props.column.label }}
            </span>
          </template>

          <template
            slot="table-row"
            slot-scope="props"
          >
            <!-- Column: ID/Exchange -->
            <span v-if="props.column.field === 'id'">
              <span class="font-weight-semibold">{{ props.row.id }}</span>
              <span
                v-if="props.row.is_an_exchange"
                class="text-danger small ml-1"
              >
                (Echange)
              </span>
            </span>

            <!-- Column: Action -->
            <span v-else-if="props.column.field === 'action'">
              <div class="d-flex align-items-center justify-content-center">
                <feather-icon
                  :id="`btn-def-${props.row.id}`"
                  icon="XCircleIcon"
                  class="text-danger cursor-pointer mr-2"
                  size="20"
                  @click="addToDefinitiveReturn(props.row)"
                />
                <b-tooltip
                  :target="`btn-def-${props.row.id}`"
                  title="Retour définitif"
                />

                <feather-icon
                  :id="`btn-relance-${props.row.id}`"
                  icon="RefreshCwIcon"
                  class="text-warning cursor-pointer"
                  size="20"
                  @click="addToRelance(props.row)"
                />
                <b-tooltip
                  :target="`btn-relance-${props.row.id}`"
                  title="Relance"
                />
              </div>
            </span>

            <!-- Column: Client Name -->
            <span v-else-if="props.column.field === 'name'">
              <span>{{ props.row.name }}</span>
            </span>

            <!-- Column: Client Phone -->
            <span v-else-if="props.column.field === 'phone1'">
              <a :href="'tel:' + props.row.phone1">{{ props.row.phone1 }}</a>
            </span>

            <!-- Column: Supplier -->
            <span v-else-if="props.column.field === 'supplier.username'">
              {{ props.formattedRow[props.column.field] }}
            </span>

            <!-- Column: Supplier Phone -->
            <span v-else-if="props.column.field === 'supplier.phone'">
              <a :href="'tel:' + (props.row.supplier && props.row.supplier.phone)">{{ props.row.supplier && props.row.supplier.phone }}</a>
            </span>

            <!-- Column: Gouvernorat -->
            <span v-else-if="props.column.field === 'governorate'">
              {{ props.formattedRow[props.column.field] }}
            </span>

            <!-- Column: Motif de retour -->
            <span v-else-if="props.column.field === 'motif_retour'">
              <div class="d-flex align-items-center">
                <v-select
                  v-model="props.row.reason_id"
                  :options="returnReasonTypes"
                  label="content"
                  :reduce="opt => opt.id"
                  append-to-body
                  class="compact-reason-select flex-grow-1"
                  placeholder="Sélectionner motif"
                  @input="(val) => updateAvailableOrderReason(props.row, val)"
                />
              </div>
            </span>

            <!-- Column: Tentatives -->
            <span v-else-if="props.column.field === 'return_number'">
              <b-badge
                pill
                :variant="props.row.return_number > 2 ? 'light-danger' : 'light-info'"
                class="font-weight-bolder"
                style="font-size: 0.9rem; padding: 0.4rem 0.8rem;"
              >
                {{ props.row.return_number || 0 }}
              </b-badge>
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <!-- Simple Pagination -->
          <template
            slot="pagination-bottom"
            slot-scope="props"
          >
            <div class="simple-pagination">
              <div class="d-flex align-items-center">
                <span class="rows-per-page-text">Rows per page</span>
                <b-form-select
                  v-model="pageLength"
                  :options="['5','10','20','50']"
                  size="sm"
                  class="rows-select"
                  @input="(value)=>props.perPageChanged({currentPerPage:value})"
                />
              </div>
              <div class="pagination-nav">
                <span
                  class="nav-link"
                  :class="{ disabled: currentPage <= 1 }"
                  @click="currentPage > 1 ? (currentPage--, props.pageChanged({currentPage: currentPage})) : null"
                >
                  Prev
                </span>
                <span class="page-info">
                  {{ props.total > 0 ? (currentPage - 1) * pageLength + 1 : 0 }} - {{ Math.min(currentPage * pageLength, props.total) }} of {{ props.total }}
                </span>
                <span
                  class="nav-link"
                  :class="{ disabled: currentPage >= Math.ceil(props.total / pageLength) }"
                  @click="currentPage < Math.ceil(props.total / pageLength) ? (currentPage++, props.pageChanged({currentPage: currentPage})) : null"
                >
                  Next
                </span>
              </div>
            </div>
          </template>
        </vue-good-table>
      </b-card>

      <!-- Summary Section -->
      <div
        v-if="definitiveReturnOrders.length > 0 || relanceOrders.length > 0"
        class="mt-3"
      >
        <b-row>
          <!-- Definitive Summary -->
          <b-col
            v-if="definitiveReturnOrders.length > 0"
            cols="12"
            class="mb-2"
          >
            <b-card
              class="summary-card-clean border"
              body-class="p-0"
            >
              <div class="summary-header-clean">
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="XCircleIcon"
                    size="18"
                    class="text-danger mr-2"
                  />
                  <span class="header-title-clean">colis retour : <strong>{{ definitiveReturnOrders.length }}</strong></span>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="clearDefinitiveReturns"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    size="14"
                    class="mr-50"
                  />
                  Tout effacer
                </b-button>
              </div>

              <vue-good-table
                :columns="returnSummaryColumns"
                :rows="definitiveReturnOrders"
                :pagination-options="{
                  enabled: false
                }"
                :max-height="'400px'"
                style-class="vgt-table pro-summary-table"
              >
                <!-- Column Filters Slot -->
                <template
                  slot="table-column"
                  slot-scope="props"
                >
                  <span>{{ props.column.label }}</span>
                </template>

                <template
                  slot="table-row"
                  slot-scope="props"
                >
                  <!-- BL -->
                  <span
                    v-if="props.column.field === 'id'"
                    class="font-weight-bold"
                  >
                    {{ props.row.id }}
                  </span>

                  <!-- Motif Retour Dropdown -->
                  <span v-else-if="props.column.field === 'motif_retour'">
                    <div class="d-flex align-items-center">
                      <v-select
                        v-model="props.row.reason_id"
                        :options="returnReasonTypes"
                        label="content"
                        :reduce="opt => opt.id"
                        append-to-body
                        class="compact-reason-select"
                        placeholder="recherche retour"
                        @input="(val) => updateOrderReason(props.row, val)"
                      />
                      <feather-icon
                        icon="EditIcon"
                        size="14"
                        class="text-danger ml-50 cursor-pointer"
                      />
                    </div>
                  </span>

                  <!-- Tentatives -->
                  <span v-else-if="props.column.field === 'return_number'">
                    <b-badge
                      pill
                      :variant="props.row.return_number > 2 ? 'light-danger' : 'light-info'"
                      class="font-weight-bolder"
                      style="font-size: 0.8rem; padding: 0.3rem 0.6rem;"
                    >
                      {{ props.row.return_number || 0 }}
                    </b-badge>
                  </span>

                  <!-- Action -->
                  <span v-else-if="props.column.field === 'action'">
                    <feather-icon
                      icon="Trash2Icon"
                      class="text-danger cursor-pointer"
                      size="18"
                      @click="removeFromDefinitiveReturn(props.row)"
                    />
                  </span>

                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>

                <!-- Simple Pagination -->
                <template
                  slot="pagination-bottom"
                  slot-scope="props"
                >
                  <div class="simple-pagination-compact">
                    <div class="rows-info">
                      Rows per page
                      <b-form-select
                        v-model="pageLength"
                        :options="['5','10','20']"
                        size="sm"
                        class="compact-rows-select"
                        @input="(value)=>props.perPageChanged({currentPerPage:value})"
                      />
                    </div>
                    <div class="nav-links">
                      <span
                        class="nav-text"
                        :class="{ disabled: currentPage <= 1 }"
                        @click="currentPage > 1 ? (currentPage--, props.pageChanged({currentPage: currentPage})) : null"
                      >Prev</span>
                      <span class="nav-page-info">{{ props.total > 0 ? (currentPage - 1) * pageLength + 1 : 0 }} - {{ Math.min(currentPage * pageLength, props.total) }} of {{ props.total }}</span>
                      <span
                        class="nav-text"
                        :class="{ disabled: currentPage >= Math.ceil(props.total / pageLength) }"
                        @click="currentPage < Math.ceil(props.total / pageLength) ? (currentPage++, props.pageChanged({currentPage: currentPage})) : null"
                      >Next</span>
                    </div>
                  </div>
                </template>
              </vue-good-table>
            </b-card>
          </b-col>

          <!-- Relance Summary -->
          <b-col
            v-if="relanceOrders.length > 0"
            cols="12"
            class="mb-2"
          >
            <b-card
              class="summary-card-clean border"
              body-class="p-0"
            >
              <div class="summary-header-clean">
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="RefreshCwIcon"
                    size="18"
                    class="text-warning mr-2"
                  />
                  <span class="header-title-clean">colis relance : <strong>{{ relanceOrders.length }}</strong></span>
                </div>
                <b-button
                  variant="outline-warning"
                  size="sm"
                  @click="clearRelances"
                >
                  <feather-icon
                    icon="Trash2Icon"
                    size="14"
                    class="mr-50"
                  />
                  Tout effacer
                </b-button>
              </div>

              <vue-good-table
                :columns="relanceSummaryColumns"
                :rows="relanceOrders"
                :pagination-options="{
                  enabled: false
                }"
                :max-height="'400px'"
                style-class="vgt-table pro-summary-table"
              >
                <template
                  slot="table-row"
                  slot-scope="props"
                >
                  <!-- BL -->
                  <span
                    v-if="props.column.field === 'id'"
                    class="font-weight-bold"
                  >
                    {{ props.row.id }}
                  </span>

                  <!-- Motif Retour Dropdown -->
                  <span v-else-if="props.column.field === 'motif_retour'">
                    <div class="d-flex align-items-center">
                      <v-select
                        v-model="props.row.reason_id"
                        :options="returnReasonTypes"
                        label="content"
                        :reduce="opt => opt.id"
                        append-to-body
                        class="compact-reason-select"
                        placeholder="recherche retour"
                        @input="(val) => updateOrderReason(props.row, val)"
                      />
                      <feather-icon
                        icon="EditIcon"
                        size="14"
                        class="text-danger ml-50 cursor-pointer"
                      />
                    </div>
                  </span>

                  <!-- Tentatives -->
                  <span v-else-if="props.column.field === 'return_number'">
                    <b-badge
                      pill
                      :variant="props.row.return_number > 2 ? 'light-danger' : 'light-info'"
                      class="font-weight-bolder"
                      style="font-size: 0.8rem; padding: 0.3rem 0.6rem;"
                    >
                      {{ props.row.return_number || 0 }}
                    </b-badge>
                  </span>

                  <!-- Mode Paiement Badge -->
                  <span v-else-if="props.column.field === 'payment_method'">
                    <b-badge
                      variant="success"
                      pill
                    >E</b-badge>
                  </span>

                  <!-- Action -->
                  <span v-else-if="props.column.field === 'action'">
                    <feather-icon
                      icon="Trash2Icon"
                      class="text-danger cursor-pointer"
                      size="18"
                      @click="removeFromRelance(props.row)"
                    />
                  </span>

                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                </template>

                <!-- Simple Pagination -->
                <template
                  slot="pagination-bottom"
                  slot-scope="props"
                >
                  <div class="simple-pagination-compact">
                    <div class="rows-info">
                      Rows per page
                      <b-form-select
                        v-model="pageLength"
                        :options="['5','10','20']"
                        size="sm"
                        class="compact-rows-select"
                        @input="(value)=>props.perPageChanged({currentPerPage:value})"
                      />
                    </div>
                    <div class="nav-links">
                      <span
                        class="nav-text"
                        :class="{ disabled: currentPage <= 1 }"
                        @click="currentPage > 1 ? (currentPage--, props.pageChanged({currentPage: currentPage})) : null"
                      >Prev</span>
                      <span class="nav-page-info">{{ props.total > 0 ? (currentPage - 1) * pageLength + 1 : 0 }} - {{ Math.min(currentPage * pageLength, props.total) }} of {{ props.total }}</span>
                      <span
                        class="nav-text"
                        :class="{ disabled: currentPage >= Math.ceil(props.total / pageLength) }"
                        @click="currentPage < Math.ceil(props.total / pageLength) ? (currentPage++, props.pageChanged({currentPage: currentPage})) : null"
                      >Next</span>
                    </div>
                  </div>
                </template>
              </vue-good-table>
            </b-card>
          </b-col>
        </b-row>

        <!-- Final Submission Bar -->
        <b-card
          class="submit-bar mt-2 shadow-sm border-0 bg-primary bg-lighten-4"
          body-class="p-2 d-flex justify-content-between align-items-center"
        >
          <div>
            <h5 class="mb-0 font-weight-bolder">
              Total à valider : <span class="text-primary">{{ totalOrders }} colis</span>
            </h5>
            <small class="text-muted">Cliquez sur le bouton pour confirmer la création des retours.</small>
          </div>
          <b-button
            variant="primary"
            size="lg"
            :disabled="!canSubmit"
            class="shadow px-4"
            @click="showConfirmation"
          >
            <feather-icon
              icon="SaveIcon"
              class="mr-1"
            />
            VALIDER MAINTENANT
          </b-button>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BButton, BFormGroup, BSpinner, BAlert, BRow, BCol, BModal, BBadge, BTooltip, BFormSelect,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import vSelect from 'vue-select'
import axios from 'axios'
import { orderMatchesReference } from '@/utils/orderReference'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BButton,
    BFormGroup,
    BSpinner,
    BAlert,
    BRow,
    BCol,
    BModal,
    BBadge,
    BTooltip,
    VueGoodTable,
    vSelect,
    BFormSelect,
  },
  data() {
    return {
      isLoading: false,
      user: {},
      repository: null,
      repositories: [],
      searchQuery: '',
      pageLength: 20,
      currentPage: 1,

      // Return reasons
      returnReasonTypes: [],
      selectedReturnReasonId: null,
      selectedReturnReasonContent: null,
      selectedActionType: 'definitive', // 'definitive' or 'relance'

      // Scanning
      definitiveRef: '',
      relanceRef: '',
      scanRef: '',

      // Orders
      availableOrders: [],
      definitiveReturnOrders: [],
      relanceOrders: [],

      mainColumns: [
        {
          label: 'ACTION', field: 'action', width: '84px', sortable: false, thClass: 'text-center', tdClass: 'text-center',
        },
        {
          label: 'BL', field: 'id', width: '130px', filterOptions: { enabled: true, placeholder: 'BL' },
        },
        {
          label: 'MOTIF', field: 'motif_retour', width: '260px', filterOptions: { enabled: true, placeholder: 'motif...' },
        },
        {
          label: 'TNT', field: 'return_number', width: '74px', sortable: true, thClass: 'text-center', tdClass: 'text-center', filterOptions: { enabled: true, placeholder: '#' },
        },
        {
          label: 'CLIENT', field: 'name', width: '210px', filterOptions: { enabled: true, placeholder: 'client...' },
        },
        {
          label: 'TÉL CLIENT', field: 'phone1', width: '140px', filterOptions: { enabled: true, placeholder: 'tél...' },
        },
        {
          label: 'FOURNISSEUR', field: 'supplier.username', width: '210px', filterOptions: { enabled: true, placeholder: 'fourn...' },
        },
        {
          label: 'TÉL FOURN.', field: 'supplier.phone', width: '140px', filterOptions: { enabled: true, placeholder: 'tél...' },
        },
        {
          label: 'GOUVERNORAT', field: 'governorate', width: '180px', filterOptions: { enabled: true, placeholder: 'gouv...' },
        },
      ],

      summaryColumns: [
        {
          label: 'ACTION', field: 'action', width: '50px', sortable: false,
        },
        { label: 'BL', field: 'id', filterOptions: { enabled: true, placeholder: 'BL' } },
        { label: 'CLIENT', field: 'name', filterOptions: { enabled: true, placeholder: 'recherhe' } },
      ],

      returnSummaryColumns: [
        {
          label: '', field: 'action', width: '40px', sortable: false,
        },
        {
          label: 'BL', field: 'id', width: '100px', filterOptions: { enabled: true, placeholder: 'BL' },
        },
        {
          label: 'TNT', field: 'return_number', width: '60px', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
        { label: 'motif retour', field: 'motif_retour', filterOptions: { enabled: true, placeholder: 'recherche retour' } },
        { label: 'Client', field: 'name', filterOptions: { enabled: true, placeholder: 'recherhe' } },
        { label: 'Gouvernorat', field: 'governorate', filterOptions: { enabled: true, placeholder: 'recherche gc' } },
        { label: 'Delegation', field: 'delegation', filterOptions: { enabled: true, placeholder: 'recherche deleg' } },
      ],

      relanceSummaryColumns: [
        {
          label: '', field: 'action', width: '40px', sortable: false,
        },
        {
          label: 'BL', field: 'id', width: '100px', filterOptions: { enabled: true, placeholder: 'BL' },
        },
        {
          label: 'TNT', field: 'return_number', width: '60px', sortable: true, thClass: 'text-center', tdClass: 'text-center',
        },
        { label: 'motif retour', field: 'motif_retour', filterOptions: { enabled: true, placeholder: 'recherche retour' } },
        { label: 'Client', field: 'name', filterOptions: { enabled: true, placeholder: 'recherhe' } },
        { label: 'Gouvernorat', field: 'governorate', filterOptions: { enabled: true, placeholder: 'recherche gc' } },
        { label: 'Delegation', field: 'delegation', filterOptions: { enabled: true, placeholder: 'recherche deleg' } },
        { label: 'Telephone', field: 'phone1', filterOptions: { enabled: true, placeholder: 'recherche phone' } },
        { label: 'prix', field: 'price_ttc', filterOptions: { enabled: true, placeholder: 'rech' } },
        { label: 'Mode paiement', field: 'payment_method', filterOptions: { enabled: true, placeholder: 'rech' } },
      ],
    }
  },
  computed: {
    repositoryId() {
      if (['admin', 'superadmin', 'customer_support'].includes(this.user.role)) {
        return this.repository?.id
      }
      return this.user.repository
    },
    totalOrders() {
      return this.definitiveReturnOrders.length + this.relanceOrders.length
    },
    canSubmit() {
      // Check that all orders have a reason assigned
      const allDefinitiveHaveReasons = this.definitiveReturnOrders.every(o => o.reason_id)
      const allRelanceHaveReasons = this.relanceOrders.every(o => o.reason_id)
      return this.totalOrders > 0 && allDefinitiveHaveReasons && allRelanceHaveReasons
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.loadReturnReasonTypes()
    this.getRepositories()
    if (this.user.role === 'manager' || this.user.repository) {
      this.loadOrders()
    }
  },
  mounted() {
    this.refocusScanner()
  },
  methods: {
    refocusScanner() {
      this.$nextTick(() => {
        if (this.$refs.definitiveInput) {
          this.$refs.definitiveInput.focus()
        }
      })
    },
    async loadReturnReasonTypes() {
      try {
        const { data } = await axios.get('/api/return-vouchers/reason-types/')
        this.returnReasonTypes = data.results || data
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des motifs de retour')
      }
    },
    selectReturnReason(reason) {
      this.selectedReturnReasonId = reason.id
      this.selectedReturnReasonContent = reason.content
      this.showToast('success', `Motif sélectionné: ${reason.content}`)
    },
    async getRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching repositories', error)
      }
    },
    async loadOrders() {
      if (!this.repositoryId) return

      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/orders/orders-allowed-for-definitive-return/${this.repositoryId}/`)

        // Map the orders and set reason_id and reason_content from last_return_reason
        this.availableOrders = data.map(order => {
          if (order.last_return_reason && order.last_return_reason.return_reason) {
            // Find the matching reason type from our returnReasonTypes
            const matchingReason = this.returnReasonTypes.find(
              r => r.content === order.last_return_reason.return_reason,
            )
            if (matchingReason) {
              return {
                ...order,
                reason_id: matchingReason.id,
                reason_content: matchingReason.content,
              }
            }
          }
          return order
        })

        // Reset selections when depot changes
        this.definitiveReturnOrders = []
        this.relanceOrders = []
      } catch (error) {
        this.showToast('danger', 'Erreur lors du chargement des colis')
      } finally {
        this.isLoading = false
      }
    },

    // New unified scan handler
    handleScan() {
      if (!this.scanRef.trim()) return

      if (!this.selectedReturnReasonId) {
        this.showToast('warning', 'Veuillez sélectionner un motif de retour avant de scanner.')
        this.scanRef = ''
        return
      }

      const order = this.availableOrders.find(item => orderMatchesReference(item, this.scanRef))

      if (!order) {
        this.showToast('danger', `Colis ${this.scanRef} non trouvé dans la liste disponible`)

        this.scanRef = ''
        return
      }

      // Add to appropriate list based on selected action type
      if (this.selectedActionType === 'definitive') {
        this.addToDefinitiveReturn(order)
      } else {
        this.addToRelance(order)
      }

      this.scanRef = ''
      this.$nextTick(() => {
        if (this.$refs.scanInput) this.$refs.scanInput.focus()
      })
    },

    scanForDefinitive() {
      if (!this.definitiveRef.trim()) return

      const order = this.availableOrders.find(item => orderMatchesReference(item, this.definitiveRef))
      if (order) {
        this.addToDefinitiveReturn(order)
      } else {
        this.showToast('danger', `Colis ${this.definitiveRef} non trouvé dans la liste disponible`)
      }
      this.definitiveRef = ''
      this.$nextTick(() => {
        if (this.$refs.definitiveInput) this.$refs.definitiveInput.focus()
      })
    },
    scanForRelance() {
      if (!this.relanceRef.trim()) return

      const order = this.availableOrders.find(item => orderMatchesReference(item, this.relanceRef))
      if (order) {
        this.addToRelance(order)
      } else {
        this.showToast('danger', `Colis ${this.relanceRef} non trouvé dans la liste disponible`)
      }
      this.relanceRef = ''
      this.$nextTick(() => {
        if (this.$refs.relanceInput) this.$refs.relanceInput.focus()
      })
    },

    // Action Methods
    updateAvailableOrderReason(order, reasonId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonId)
      /* eslint-disable no-param-reassign */
      order.reason_id = reasonId
      order.reason_content = reason ? reason.content : ''
      /* eslint-enable no-param-reassign */
    },
    updateOrderReason(order, reasonId) {
      const reason = this.returnReasonTypes.find(r => r.id === reasonId)
      /* eslint-disable no-param-reassign */
      order.reason_id = reasonId
      order.reason_content = reason ? reason.content : ''
      /* eslint-enable no-param-reassign */
    },
    addToDefinitiveReturn(order) {
      if (!order.reason_id) {
        this.showToast('warning', 'Veuillez sélectionner un motif de retour pour ce colis.')
        return
      }
      const newOrder = {
        ...order,
        reason_id: order.reason_id,
        reason_content: order.reason_content,
      }
      this.definitiveReturnOrders.push(newOrder)
      this.availableOrders = this.availableOrders.filter(o => o.id !== order.id)
      this.showToast('success', `Colis ${order.id} ajouté aux retours définitifs.`)
    },
    addToRelance(order) {
      if (!order.reason_id) {
        this.showToast('warning', 'Veuillez sélectionner un motif de retour pour ce colis.')
        return
      }
      const newOrder = {
        ...order,
        reason_id: order.reason_id,
        reason_content: order.reason_content,
      }
      this.relanceOrders.push(newOrder)
      this.availableOrders = this.availableOrders.filter(o => o.id !== order.id)
      this.showToast('success', `Colis ${order.id} ajouté aux relances.`)
    },
    removeFromDefinitiveReturn(order) {
      this.definitiveReturnOrders = this.definitiveReturnOrders.filter(o => o.id !== order.id)
      this.availableOrders.push(order)
    },
    removeFromRelance(order) {
      this.relanceOrders = this.relanceOrders.filter(o => o.id !== order.id)
      this.availableOrders.push(order)
    },
    clearDefinitiveReturns() {
      this.availableOrders.push(...this.definitiveReturnOrders)
      this.definitiveReturnOrders = []
    },
    clearRelances() {
      this.availableOrders.push(...this.relanceOrders)
      this.relanceOrders = []
    },

    showConfirmation() {
      this.$refs.confirmationModal.show()
    },
    getTargetStatusLabel() {
      const labels = []
      if (this.definitiveReturnOrders.length > 0) labels.push('Retour définitif')
      if (this.relanceOrders.length > 0) labels.push('Relance')
      return labels.join(' & ')
    },
    async confirmAndSubmit() {
      this.isLoading = true

      // Batch promises
      const requests = []

      if (this.definitiveReturnOrders.length > 0) {
        const orderReasons = {}
        this.definitiveReturnOrders.forEach(o => {
          orderReasons[o.id] = o.reason_content
        })
        requests.push(axios.post('/api/return-vouchers/create-definitive-return-voucher/', {
          orders: this.definitiveReturnOrders.map(o => o.id),
          repository: this.repositoryId,
          reason: this.definitiveReturnOrders[0]?.reason_content || '',
          order_reasons: orderReasons,
          target_status: 22,
        }))
      }

      if (this.relanceOrders.length > 0) {
        const orderReasons = {}
        this.relanceOrders.forEach(o => {
          orderReasons[o.id] = o.reason_content
        })
        requests.push(axios.post('/api/return-vouchers/create-definitive-return-voucher/', {
          orders: this.relanceOrders.map(o => o.id),
          repository: this.repositoryId,
          reason: this.relanceOrders[0]?.reason_content || '',
          order_reasons: orderReasons,
          target_status: 23,
        }))
      }

      try {
        await Promise.all(requests)
        this.showToast('success', 'Tous les retours ont été validés avec succès.')
        this.definitiveReturnOrders = []
        this.relanceOrders = []
        this.loadOrders()
      } catch (error) {
        const msg = error.response?.data?.error || 'Une erreur est survenue lors de la validation.'
        this.showToast('danger', msg)
      } finally {
        this.isLoading = false
      }
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Action Retour',
          icon: variant === 'success' ? 'CheckIcon' : 'AlertTriangleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import '@core/scss/vue/libs/vue-good-table.scss';

.cursor-pointer { cursor: pointer; }

// ─── Table card overflow ───────────────────────────────────────────────────────
::v-deep .overflow-auto {
  overflow-x: auto !important;
}

// ─── Table header bar ─────────────────────────────────────────────────────────
.table-header-title-simple {
  padding: 0.9rem 1.25rem;
  background-color: #fff;
  border-bottom: 2px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h5 {
    font-size: 1rem;
    font-weight: 600;
    color: #5e5873;
    margin: 0;
  }
}

// ─── Main pro-table ───────────────────────────────────────────────────────────
::v-deep .pro-table {
  border: none;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  width: 100% !important;
  min-width: 1428px;

  table {
    width: 100% !important;
    table-layout: fixed;
  }

  thead th {
    background-color: #f3f2f7 !important;
    color: #2c3e50 !important;
    font-weight: 700 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase !important;
    letter-spacing: 0.4px;
    padding: 0.75rem 0.85rem !important;
    border-bottom: 2px solid #ddd !important;
    border-top: none !important;
    white-space: nowrap;
    vertical-align: middle;
  }

  tbody td {
    padding: 0.75rem 0.85rem !important;
    vertical-align: middle;
    color: #495057;
    font-size: 0.875rem;
    border-bottom: 1px solid #e9ecef !important;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  tbody tr {
    background-color: #fff;
    transition: background 0.12s ease;

    &:hover {
      background-color: #f6f4ff !important;
    }

    &:last-child td {
      border-bottom: none !important;
    }
  }

  .vgt-input {
    padding: 0.35rem 0.55rem;
    font-size: 0.8rem;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    background-color: #fff;
    width: 100%;
    min-width: 0;
    transition: border-color 0.2s ease;

    &:focus {
      outline: none;
      border-color: #7367f0;
      box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.12);
    }
  }
}

// ─── Summary tables ───────────────────────────────────────────────────────────
.summary-card-clean {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.07);
}

.summary-header-clean {
  padding: 0.85rem 1.25rem;
  background-color: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-title-clean {
    font-size: 0.95rem;
    color: #2c3e50;
    font-weight: 500;

    strong {
      font-weight: 700;
      color: #000;
    }
  }
}

::v-deep .pro-summary-table {
  border: none;

  thead th {
    background-color: #f8f9fa !important;
    color: #495057 !important;
    font-weight: 700 !important;
    font-size: 0.75rem !important;
    text-transform: uppercase !important;
    letter-spacing: 0.3px;
    padding: 0.65rem 0.9rem !important;
    border-bottom: 2px solid #e0e0e0 !important;
    border-top: none !important;
    white-space: nowrap;
  }

  tbody td {
    padding: 0.7rem 0.9rem !important;
    vertical-align: middle;
    color: #495057;
    font-size: 0.875rem;
    border-bottom: 1px solid #e9ecef !important;
  }

  tbody tr {
    background-color: #fff;

    &:hover { background-color: #f8f9fa !important; }
    &:last-child td { border-bottom: none !important; }
  }

  .vgt-input {
    padding: 0.35rem 0.55rem;
    font-size: 0.8rem;
    border: 1px solid #d0d0d0;
    border-radius: 4px;
    width: 100%;

    &:focus {
      outline: none;
      border-color: #7367f0;
      box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.1);
    }
  }
}

// ─── Reason select ────────────────────────────────────────────────────────────
.compact-reason-select {
  width: 100%;
  min-width: 0;
  max-width: 240px;
  z-index: 20;
  .vs__dropdown-toggle {
    border-color: #d8d6de;
    min-height: 34px;
    padding: 1px 0;
    border-radius: 0.357rem;
  }
  .vs__selected {
    font-size: 0.85rem;
    margin: 1px 2px;
    padding: 2px 6px;
  }
  .vs__search {
    padding: 4px 0;
    font-size: 0.85rem;
  }
  .vs__actions {
    padding: 2px 6px;
  }
  .vs__dropdown-menu {
    z-index: 2000;
  }
}

// ─── Pagination ───────────────────────────────────────────────────────────────
.simple-pagination-compact,
.simple-pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.65rem 1rem;
  background-color: #fff;
  border-top: 1px solid #ebe9f1;
  font-size: 0.85rem;

  .rows-info { display: flex; align-items: center; gap: 8px; color: #6e6b7b; }
  .compact-rows-select,
  .rows-select { width: 65px !important; height: 28px !important; padding: 0 4px !important; border: 1px solid #d8d6de; border-radius: 4px; }

  .nav-links,
  .pagination-nav { display: flex; align-items: center; gap: 12px; }

  .nav-text,
  .nav-link {
    font-weight: 600;
    color: #7367f0;
    cursor: pointer;

    &.disabled { color: #b9b9c3; cursor: not-allowed; }
    &:hover:not(.disabled) { color: #5e50d4; }
  }

  .nav-page-info,
  .page-info { color: #333; font-weight: 500; }
}

// ─── Submit bar ───────────────────────────────────────────────────────────────
.submit-bar {
  border-left: 5px solid #7367f0 !important;
}

// ─── Modal helpers ────────────────────────────────────────────────────────────
.border-left-danger  { border-left: 5px solid #ea5455; }
.border-left-warning { border-left: 5px solid #ff9f43; }
</style>
